import { Calendar } from "../calendar";
import type { CalendarHandle } from "../calendar/use-calendar-handle";
import type { CalendarDate } from "@internationalized/date";
import type { CSSProperties, Ref, RefObject } from "react";
import type { ReferenceType } from "@floating-ui/react";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import type { FooterProperties } from "./footer";
import { Footer } from "./footer";
import type { MonthProperties } from "../calendar/month";
import type { RenderDate } from "../calendar/day";
import { useMatchMedia } from "../../hooks/use-match-media";
import { BREAKPOINT_MD } from "@jetblue/kit-tailwind-config/theme";

export const useDropdown = ({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}) => {
  const { refs, floatingStyles, context } = useFloating({
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    onOpenChange,
    open: isOpen,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
  });

  const isMobile = !useMatchMedia(`(min-width: ${BREAKPOINT_MD})`);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useDismiss(context, { enabled: !isMobile }),
  ]);

  return {
    floating: {
      style: floatingStyles,
      ...getFloatingProps(),
    },
    refs: refs as {
      reference: RefObject<ReferenceType | null>;
      floating: RefObject<HTMLElement | null>;
      setReference: (node: ReferenceType | null) => void;
      setFloating: (node: HTMLElement | null) => void;
    },
    target: {
      ...getReferenceProps(),
    },
  };
};
export type DropdownProperties = {
  calendarRef?: RefObject<CalendarHandle | null>;
  endDate: CalendarDate;
  footerSlot?: FooterProperties["children"];
  isOpen: boolean;
  labels: { done: string };
  onDateClick: (date: CalendarDate) => void;
  rangeEnd?: CalendarDate | undefined;
  rangeStart: CalendarDate | undefined;
  ref?: Ref<HTMLInputElement>;
  startDate: CalendarDate;
  style?: CSSProperties | undefined;
  renderDate?: RenderDate;
} & FooterProperties &
  Pick<MonthProperties, "highlightRange">;
export const Dropdown = ({
  calendarRef,
  endDate,
  footerSlot,
  highlightRange,
  isOpen,
  labels,
  onDateClick,
  onDone,
  rangeEnd,
  rangeStart,
  ref,
  startDate,
  renderDate,
  style,
  ...rest
}: DropdownProperties) => {
  if (!isOpen) {
    return null;
  }
  return (
    <div
      className="z-100000 hidden max-w-3xl overflow-hidden border border-light-gray bg-white md:block"
      ref={ref}
      style={style}
      {...rest}
      tabIndex={-1}
    >
      <div>
        <Calendar
          calendarRef={calendarRef}
          endDate={endDate}
          highlightRange={highlightRange}
          onDateClick={onDateClick}
          rangeEnd={rangeEnd}
          rangeStart={rangeStart}
          renderDate={renderDate}
          startDate={startDate}
        />
      </div>
      <Footer labels={{ done: labels.done }} onDone={onDone}>
        {footerSlot}
      </Footer>
    </div>
  );
};
Dropdown.displayName = "DatePicker.Dropdown";
